import type { ZodError, ZodIssue } from 'zod'

// Pretty print a path like products[0].return_reasons
function pathToString(path: (string | number)[]): string {
  return (
    path
      .map(seg => (typeof seg === 'number' ? `[${seg}]` : `.${seg}`))
      .join('')
      .replace(/^\./, '') || '<root>'
  )
}

function typeName(v: unknown): string {
  if (v === null) return 'null'
  if (Array.isArray(v)) return 'array'
  return typeof v // "string" | "number" | "boolean" | "object" | "undefined" | "function" | "symbol" | "bigint"
}

// Tiny safe getter to extract the value at a given path from the original input
function getAtPath(data: unknown, path: (string | number)[]): unknown {
  return path.reduce(
    (acc: any, seg) => (acc != null ? acc[seg as any] : undefined),
    data as any
  )
}

// Serialize a short, single-line preview of a value
function preview(val: unknown, max = 160): string {
  try {
    const json = JSON.stringify(val, (_k, v) =>
      typeof v === 'bigint' ? v.toString() : v
    )
    if (!json) return String(val)
    return json.length > max ? json.slice(0, max) + '…' : json
  } catch {
    // circular, functions, etc.
    const t = typeName(val)
    if (t === 'object' && val) return '[object]'
    return String(val)
  }
}

// Format a single issue, optionally including the received value from the input
function formatIssue(issue: ZodIssue, data?: unknown): string {
  const loc = pathToString(issue.path)
  const seen = data !== undefined ? getAtPath(data, issue.path) : undefined
  const seenType =
    data !== undefined
      ? typeName(seen)
      : issue.code === 'invalid_type'
      ? String((issue as any).received)
      : undefined
  const seenPreview = data !== undefined ? `; value=${preview(seen)}` : ''

  const base = (extra: string) =>
    `${loc}: ${extra}${seenType ? `; received ${seenType}` : ''}${seenPreview}`

  switch (issue.code) {
    case 'invalid_type': {
      const i = issue as Extract<ZodIssue, { code: 'invalid_type' }>
      return base(
        `expected ${i.expected}${issue.message ? `; ${issue.message}` : ''}`
      )
    }
    case 'invalid_enum_value': {
      const i = issue as Extract<ZodIssue, { code: 'invalid_enum_value' }>
      return base(`expected one of ${i.options.map(String).join(', ')}`)
    }
    case 'unrecognized_keys': {
      const i = issue as Extract<ZodIssue, { code: 'unrecognized_keys' }>
      return base(`unrecognized keys: ${i.keys.join(', ')}`)
    }
    case 'invalid_union': {
      const i = issue as Extract<ZodIssue, { code: 'invalid_union' }>
      // Summarize the shortest nested error for brevity
      const best = i.unionErrors.reduce((a, b) =>
        a.issues.length <= b.issues.length ? a : b
      )
      const nested = best.issues
        .map(u => `  - ${formatIssue(u as ZodIssue, data)}`)
        .join('\n')
      return `${loc}: invalid union; closest match had ${best.issues.length} issue(s):\n${nested}`
    }
    case 'invalid_union_discriminator': {
      const i = issue as Extract<
        ZodIssue,
        { code: 'invalid_union_discriminator' }
      >
      return base(
        `invalid discriminator; expected one of ${i.options
          .map(String)
          .join(', ')}`
      )
    }
    case 'invalid_literal': {
      const i = issue as Extract<ZodIssue, { code: 'invalid_literal' }>
      return base(`expected literal ${preview(i.expected)}`)
    }
    case 'too_small': {
      const i = issue as Extract<ZodIssue, { code: 'too_small' }>
      const bound = i.exact ? 'exactly' : i.inclusive ? 'at least' : 'more than'
      return base(
        `${i.type} too small; expected ${bound} ${i.minimum}${
          i.message ? `; ${i.message}` : ''
        }`
      )
    }
    case 'too_big': {
      const i = issue as Extract<ZodIssue, { code: 'too_big' }>
      const bound = i.exact ? 'exactly' : i.inclusive ? 'at most' : 'less than'
      return base(
        `${i.type} too big; expected ${bound} ${i.maximum}${
          i.message ? `; ${i.message}` : ''
        }`
      )
    }
    case 'invalid_string': {
      const i = issue as Extract<ZodIssue, { code: 'invalid_string' }>
      return base(
        `invalid string${i.validation ? `; expected ${i.validation}` : ''}`
      )
    }
    case 'invalid_date': {
      return base(`invalid date`)
    }
    case 'invalid_intersection_types': {
      return base(`invalid intersection types`)
    }
    case 'not_multiple_of': {
      const i = issue as Extract<ZodIssue, { code: 'not_multiple_of' }>
      return base(`number must be a multiple of ${i.multipleOf}`)
    }
    case 'custom': {
      return base(issue.message || 'custom validation failed')
    }
    default: {
      // Fallback for any future Zod codes
      return base(issue.message || `validation failed (${issue.code})`)
    }
  }
}

/**
 * Formats a ZodError into readable lines.
 * Pass the original `input` as the second param to include the actual received values.
 *
 * Example output:
 *   products[0].return_reasons: expected array; received undefined
 *   customer.email: invalid string; expected email; received string; value="foo@"
 */
export function formatZodError(err: ZodError, input?: unknown): string {
  return err.issues.map(issue => formatIssue(issue, input)).join('\n')
}
